/**
 * Goods Receipt Form Page
 * Epic 21: Új bevételezés létrehozása
 *
 * Features:
 *   - Supplier selector (search existing suppliers)
 *   - Optional: link to existing purchase advice (auto-fills items)
 *   - Item rows: product search, expected qty, received qty, purchase category selector
 *   - Package splitting toggle (csomagbontás): packageQty x unitsPerPackage
 *   - "Mentés" button → POST /receipts
 */

import type { PurchaseCategory } from '@/api/goods-receipt';
import type { Partner } from '@/api/partners';
import { PartnerSearchSelect } from '@/components/partners';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useAvizo, useCreateReceipt, usePendingAvizos } from '@/hooks/useGoodsReceipt';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface FormItem {
  avizoItemId?: string;
  productId: string;
  productCode: string;
  productName: string;
  expectedQuantity: number;
  receivedQuantity: number;
  unitPrice: number;
  locationCode: string;
  purchaseCategory: PurchaseCategory;
  usePackageSplitting: boolean;
  packageQuantity: number;
  unitsPerPackage: number;
}

const PURCHASE_CATEGORIES: Array<{ value: PurchaseCategory; labelKey: string }> = [
  { value: 'ARU', labelKey: 'goodsReceipt.form.categories.aru' },
  { value: 'BERUHAZAS', labelKey: 'goodsReceipt.form.categories.beruhazas' },
  { value: 'BERENDEZES', labelKey: 'goodsReceipt.form.categories.berendezes' },
  { value: 'EGYEB', labelKey: 'goodsReceipt.form.categories.egyeb' },
  { value: 'REZSI', labelKey: 'goodsReceipt.form.categories.rezsi' },
];

function createEmptyItem(): FormItem {
  return {
    productId: '',
    productCode: '',
    productName: '',
    expectedQuantity: 1,
    receivedQuantity: 1,
    unitPrice: 0,
    locationCode: '',
    purchaseCategory: 'ARU',
    usePackageSplitting: false,
    packageQuantity: 1,
    unitsPerPackage: 1,
  };
}

export function GoodsReceiptFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const avizoId = searchParams.get('avizoId') ?? undefined;

  const createMutation = useCreateReceipt();
  const { avizos } = usePendingAvizos();
  const { avizo: linkedAvizo } = useAvizo(avizoId);

  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [selectedAvizoId, setSelectedAvizoId] = useState(avizoId ?? '');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<FormItem[]>([createEmptyItem()]);

  // Auto-fill from avizo when linked
  useEffect(() => {
    if (!linkedAvizo) return;

    setSelectedPartner({
      id: linkedAvizo.supplierId,
      name: linkedAvizo.supplierName,
    } as Partner);
    setSelectedAvizoId(linkedAvizo.id);
    setNotes(linkedAvizo.notes ?? '');
    setItems(
      linkedAvizo.items.map(ai => ({
        avizoItemId: ai.id,
        productId: ai.productId,
        productCode: ai.productCode,
        productName: ai.productName,
        expectedQuantity: ai.expectedQuantity,
        receivedQuantity: ai.expectedQuantity,
        unitPrice: ai.unitPrice,
        locationCode: '',
        purchaseCategory: 'ARU' as PurchaseCategory,
        usePackageSplitting: false,
        packageQuantity: 1,
        unitsPerPackage: 1,
      }))
    );
  }, [linkedAvizo]);

  const addItem = () => {
    setItems(prev => [...prev, createEmptyItem()]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, updates: Partial<FormItem>) => {
    setItems(prev => prev.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  };

  const handleAvizoSelect = (avizoIdStr: string) => {
    setSelectedAvizoId(avizoIdStr);
    if (!avizoIdStr) return;

    const selectedAvizo = avizos.find(a => a.id === avizoIdStr);
    if (!selectedAvizo) return;

    setSelectedPartner({
      id: selectedAvizo.supplierId,
      name: selectedAvizo.supplierName,
    } as Partner);
    setNotes(selectedAvizo.notes ?? '');
    setItems(
      selectedAvizo.items.map(ai => ({
        avizoItemId: ai.id,
        productId: ai.productId,
        productCode: ai.productCode,
        productName: ai.productName,
        expectedQuantity: ai.expectedQuantity,
        receivedQuantity: ai.expectedQuantity,
        unitPrice: ai.unitPrice,
        locationCode: '',
        purchaseCategory: 'ARU' as PurchaseCategory,
        usePackageSplitting: false,
        packageQuantity: 1,
        unitsPerPackage: 1,
      }))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const input = {
      supplierId: selectedPartner?.id ?? '',
      supplierName: selectedPartner?.name ?? '',
      purchaseAdviceId: selectedAvizoId || undefined,
      notes: notes || undefined,
      items: items.map(item => ({
        avizoItemId: item.avizoItemId,
        productId: item.productId,
        productCode: item.productCode,
        productName: item.productName,
        expectedQuantity: item.expectedQuantity,
        receivedQuantity: item.usePackageSplitting
          ? item.packageQuantity * item.unitsPerPackage
          : item.receivedQuantity,
        unitPrice: item.unitPrice,
        locationCode: item.locationCode || undefined,
        purchaseCategory: item.purchaseCategory,
        packageQuantity: item.usePackageSplitting ? item.packageQuantity : undefined,
        unitsPerPackage: item.usePackageSplitting ? item.unitsPerPackage : undefined,
      })),
    };

    const result = await createMutation.mutateAsync(input);
    const receiptData = result.data;
    navigate(`/goods-receipt/${receiptData.id}`);
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/goods-receipt')}
            className="mb-2 text-sm text-muted-foreground hover:text-foreground"
          >
            &larr; {t('goodsReceipt.detail.backToList')}
          </button>
          <h1 className="text-2xl font-bold text-foreground">{t('goodsReceipt.form.title')}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Link to Avizo */}
        {avizos.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('goodsReceipt.form.linkAvizo')}
              </label>
              <select
                value={selectedAvizoId}
                onChange={e => handleAvizoSelect(e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-foreground px-3 py-2"
              >
                <option value="">{t('goodsReceipt.form.noAvizo')}</option>
                {avizos.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.supplierName} — {new Date(a.expectedDate).toLocaleDateString('hu-HU')} (
                    {a.items.length} {t('goodsReceipt.form.itemsLabel')})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('goodsReceipt.form.avizoHint')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Supplier selector */}
        <Card>
          <CardContent className="pt-6">
            <label className="mb-2 block text-sm font-medium text-foreground">
              {t('goodsReceipt.form.supplier')} *
            </label>
            <PartnerSearchSelect
              variant="list"
              value={selectedPartner}
              onSelect={partner => {
                setSelectedPartner(partner);
              }}
              placeholder={t('goodsReceipt.form.supplierPlaceholder')}
            />
            {selectedPartner && (
              <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
                <p className="text-sm font-medium text-foreground">{selectedPartner.name}</p>
                {selectedPartner.taxNumber && (
                  <p className="text-xs text-muted-foreground">
                    {t('invoice.taxNumber')}: {selectedPartner.taxNumber}
                  </p>
                )}
                {selectedPartner.address && (
                  <p className="text-xs text-muted-foreground">{selectedPartner.address}</p>
                )}
              </div>
            )}
            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('goodsReceipt.form.notes')}
              </label>
              <Input
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={t('goodsReceipt.form.notesPlaceholder')}
              />
            </div>
          </CardContent>
        </Card>

        {/* Items */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-medium text-foreground">{t('goodsReceipt.form.items')}</h2>
            <Button type="button" variant="outline" onClick={addItem}>
              {t('goodsReceipt.form.addItem')}
            </Button>
          </div>

          <div className="space-y-4">
            {items.map((item, index) => (
              <Card key={index}>
                <CardContent className="pt-4">
                  {/* Row 1: Product info */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">
                        {t('goodsReceipt.form.productCode')}
                      </label>
                      <Input
                        type="text"
                        value={item.productCode}
                        onChange={e => updateItem(index, { productCode: e.target.value })}
                        placeholder={t('goodsReceipt.form.productCodePlaceholder')}
                        required
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">
                        {t('goodsReceipt.form.productName')}
                      </label>
                      <Input
                        type="text"
                        value={item.productName}
                        onChange={e => updateItem(index, { productName: e.target.value })}
                        placeholder={t('goodsReceipt.form.productNamePlaceholder')}
                        required
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">
                        {t('goodsReceipt.form.expectedQty')}
                      </label>
                      <Input
                        type="number"
                        value={item.expectedQuantity}
                        onChange={e =>
                          updateItem(index, {
                            expectedQuantity: parseInt(e.target.value, 10) || 0,
                          })
                        }
                        min={0}
                        required
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">
                        {t('goodsReceipt.form.receivedQty')}
                      </label>
                      <Input
                        type="number"
                        value={
                          item.usePackageSplitting
                            ? item.packageQuantity * item.unitsPerPackage
                            : item.receivedQuantity
                        }
                        onChange={e => {
                          if (!item.usePackageSplitting) {
                            updateItem(index, {
                              receivedQuantity: parseInt(e.target.value, 10) || 0,
                            });
                          }
                        }}
                        min={0}
                        required
                        disabled={item.usePackageSplitting}
                        className="text-sm"
                      />
                    </div>
                  </div>

                  {/* Row 2: Price, category, location */}
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">
                        {t('goodsReceipt.form.unitPrice')}
                      </label>
                      <Input
                        type="number"
                        value={item.unitPrice}
                        onChange={e =>
                          updateItem(index, {
                            unitPrice: parseInt(e.target.value, 10) || 0,
                          })
                        }
                        min={0}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">
                        {t('goodsReceipt.form.purchaseCategory')}
                      </label>
                      <select
                        value={item.purchaseCategory}
                        onChange={e =>
                          updateItem(index, {
                            purchaseCategory: e.target.value as PurchaseCategory,
                          })
                        }
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-foreground px-3 py-2 text-sm"
                      >
                        {PURCHASE_CATEGORIES.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {t(cat.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">
                        {t('goodsReceipt.form.locationCode')}
                      </label>
                      <Input
                        type="text"
                        value={item.locationCode}
                        onChange={e => updateItem(index, { locationCode: e.target.value })}
                        placeholder={t('goodsReceipt.form.locationPlaceholder')}
                        className="text-sm"
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      {items.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-300 dark:border-red-800 dark:hover:bg-red-900/20"
                        >
                          {t('common.remove')}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Package splitting toggle */}
                  <div className="mt-3 border-t pt-3 dark:border-gray-700">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={item.usePackageSplitting}
                        onChange={e => updateItem(index, { usePackageSplitting: e.target.checked })}
                        className="h-4 w-4 rounded"
                      />
                      <span className="text-sm text-foreground">
                        {t('goodsReceipt.form.packageSplitting')}
                      </span>
                    </label>

                    {item.usePackageSplitting && (
                      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div>
                          <label className="mb-1 block text-xs text-muted-foreground">
                            {t('goodsReceipt.form.packageQty')}
                          </label>
                          <Input
                            type="number"
                            value={item.packageQuantity}
                            onChange={e =>
                              updateItem(index, {
                                packageQuantity: parseInt(e.target.value, 10) || 1,
                              })
                            }
                            min={1}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs text-muted-foreground">
                            {t('goodsReceipt.form.unitsPerPackage')}
                          </label>
                          <Input
                            type="number"
                            value={item.unitsPerPackage}
                            onChange={e =>
                              updateItem(index, {
                                unitsPerPackage: parseInt(e.target.value, 10) || 1,
                              })
                            }
                            min={1}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs text-muted-foreground">
                            {t('goodsReceipt.form.totalUnits')}
                          </label>
                          <div className="rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-foreground dark:bg-gray-800">
                            {item.packageQuantity * item.unitsPerPackage} {t('common.piece')}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => navigate('/goods-receipt')}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || !selectedPartner || items.length === 0}
            className="bg-primary hover:bg-primary/90"
          >
            {createMutation.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </div>

        {createMutation.isError && (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300">
            {createMutation.error?.message ?? t('common.error')}
          </div>
        )}
      </form>
    </div>
  );
}
